import { toast } from "@/v2-ui/toast"
import ActionMenu from "@/v2-backoffice-client/components/actionMenu"
import { useLicenseAddOnRevokeMutation } from "@/v2-backoffice-client/license/addOns/license.addOns.revoke.mutation"
import { evictLicenseAddOnQueries } from "@/v2-backoffice-client/license/license.utils"
import type { LicenseAddOnsQuery } from "@/v2-backoffice-client/license/addOns/__types__/license.addOns.query"

export type LicenseAddOnTableRowToolbarProps = {
  addOn: LicenseAddOnsQuery["getCustomerAddOns"][number]
  customerId: number
  /** The customer, by name, for the confirmation. */
  who: string
}

// Row actions on a customer's add-on: open the add-on's panel (what it
// unlocks, who else has it) and Remove. There is nothing to edit on the
// row itself: an add-on has no settings, it rides whichever license the
// customer holds for its product.
function LicenseAddOnTableRowToolbar(props: LicenseAddOnTableRowToolbarProps) {
  const { addOn, customerId, who } = props

  const [revoke, { loading, error }] = useLicenseAddOnRevokeMutation({
    update: evictLicenseAddOnQueries,
    onCompleted() {
      toast({
        variant: "success",
        title: "Add-on removed",
        description: `${who} no longer has ${addOn.AddOnName}.`,
      })
    },
  })

  return (
    <ActionMenu>
      <ActionMenu.Trigger />
      <ActionMenu.Content>
        <ActionMenu.LinkItem
          iconSrc="expand"
          to={`>>?v=addOn:${addOn.AddOnId}`}
          data-feature-code="UI_BackofficeCustomer_AddOns_Row_Open"
        >
          Open add-on
        </ActionMenu.LinkItem>

        <ActionMenu.Separator />
        <ActionMenu.ConfirmItem
          iconSrc="trash"
          variant="destructive"
          isLoading={loading}
          error={error}
          errorTitle="Failed to remove the add-on"
          confirmTitle="Remove add-on"
          confirmDescription={`Remove ${addOn.AddOnName} from ${who}? Their users lose what it unlocks; their licenses are untouched.`}
          confirmText="Remove"
          confirmVariant="destructive"
          data-feature-code="UI_BackofficeLicense_AddOns_Revoke"
          onConfirm={async () => {
            await revoke({ variables: { customerId, addOnId: addOn.AddOnId } })
          }}
        >
          Remove
        </ActionMenu.ConfirmItem>
      </ActionMenu.Content>
    </ActionMenu>
  )
}

export default LicenseAddOnTableRowToolbar
